import { inject, Injectable } from '@angular/core';

import { WalletIdentityStore } from '../store';
import { WALLET_CONNECTOR } from '../tokens';

/** Result of reconciling the connected wallet network with a target vault chain. */
export enum NetworkSwitchOutcome {
  /** No connected wallet, so no switch was attempted. */
  Skipped = 'skipped',
  /** Wallet was already on the target chain. */
  Matched = 'matched',
  /** Wallet successfully switched to the target chain. */
  Switched = 'switched',
  /** Switch was rejected, failed, or left the wallet on a different chain. */
  Declined = 'declined',
}

@Injectable({ providedIn: 'root' })
export class WalletNetworkSwitchService {
  readonly #connector = inject(WALLET_CONNECTOR);
  readonly #walletIdentityStore = inject(WalletIdentityStore);

  /** Ensures the connected wallet is on the given vault chain, prompting a switch when needed. */
  async ensureVaultNetwork(vaultChainId: number): Promise<NetworkSwitchOutcome> {
    const identity = this.#walletIdentityStore.identity();

    if (!identity) {
      return NetworkSwitchOutcome.Skipped;
    }

    if (identity.chainId === vaultChainId) {
      return NetworkSwitchOutcome.Matched;
    }

    try {
      await this.#connector.switchToChain(vaultChainId);
    } catch {
      return NetworkSwitchOutcome.Declined;
    }

    const switchedIdentity = await this.#connector.getConnectedIdentity().catch(() => null);

    return switchedIdentity?.chainId === vaultChainId ? NetworkSwitchOutcome.Switched : NetworkSwitchOutcome.Declined;
  }
}
